"use client";

import { useState } from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import { humanizeTool } from "../humanizeTool";
import type { RichConfirm } from "@/lib/caonabo/rich-content";

type Props = {
  confirm: RichConfirm;
  /** Callback al click en Sí — manda mensaje al chat con instrucción literal. */
  onConfirm: (toolName: string, pendingInput: Record<string, unknown>) => void;
  /** Callback al click en No. */
  onCancel: () => void;
};

export default function ChatConfirmCard({ confirm, onConfirm, onCancel }: Props) {
  const [decided, setDecided] = useState<"yes" | "no" | null>(null);

  return (
    <div
      className="caonabo-fade-in rounded-xl border overflow-hidden"
      style={{
        background: "rgba(245,158,11,0.06)",
        borderColor: "rgba(245,158,11,0.30)",
      }}
    >
      <div className="px-3 py-2 flex items-center gap-2"
        style={{ background: "rgba(245,158,11,0.10)", borderBottom: "1px solid rgba(245,158,11,0.20)" }}
      >
        <AlertTriangle size={12} className="text-amber-400 shrink-0" />
        <span className="text-[11px] font-bold text-amber-300">
          Confirmación requerida — {humanizeTool(confirm.toolName)}
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        <p className="text-[11.5px] text-text">{confirm.prompt}</p>

        {confirm.warning && (
          <p className="text-[10.5px] text-amber-300/90 inline-flex items-start gap-1 rounded bg-amber-500/10 px-2 py-1">
            <AlertTriangle size={10} className="shrink-0 mt-0.5" />
            <span>{confirm.warning}</span>
          </p>
        )}

        {/* Resumen de la operación */}
        <div
          className="rounded p-2 text-[10px] grid grid-cols-2 gap-x-2 gap-y-0.5"
          style={{ background: "rgba(255,255,255,0.04)" }}
        >
          {Object.entries(confirm.summary).slice(0, 10).map(([k, v]) => (
            <div key={k} className="contents">
              <span className="text-text-muted truncate">{humanize(k)}</span>
              <span className="text-text tabular-nums truncate">{stringify(v)}</span>
            </div>
          ))}
        </div>

        {/* Botones de decisión */}
        {decided === null && (
          <div className="flex gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => {
                setDecided("yes");
                onConfirm(confirm.toolName, confirm.pendingInput);
              }}
              className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded text-white"
              style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)" }}
            >
              <Check size={11} /> Sí, confirmar
            </button>
            <button
              type="button"
              onClick={() => {
                setDecided("no");
                onCancel();
              }}
              className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded"
              style={{
                background: "rgba(239,68,68,0.10)",
                color: "#ef4444",
                border: "1px solid rgba(239,68,68,0.30)",
              }}
            >
              <X size={11} /> No, cancelar
            </button>
          </div>
        )}

        {decided === "yes" && (
          <p className="text-[10px] text-emerald-400 inline-flex items-center gap-1">
            <Check size={10} /> Confirmado — ejecutando…
          </p>
        )}
        {decided === "no" && (
          <p className="text-[10px] text-text-muted inline-flex items-center gap-1">
            <X size={10} /> Operación cancelada.
          </p>
        )}
      </div>
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  return String(v);
}
